// Pricing's pieces on the Products screens (Sprint 4; design/screens/products.html; UC-PRICING-001, -007, -011, -012): what the
// band or markup that applies at the acting branch gives for a product's cost there, with whose it is — the selected product's
// panel reads "Markup band R 0.00–R 500.00 → 400% · tenant default band (no Ray-Ban override)" and "R 2 450.00 incl. VAT · from
// band" (TC-PRICING-111), the product form offers "Band for R 490.00: R 2 450.00 (400%)" with Use beside its Price
// (TC-PRICING-114) — and the price-list export of the ticked products or the filtered tab (TC-PRICING-118). Every figure is
// pricing's answer through endpoints.pricing.ts; these screens hold no pricing rule and do no arithmetic on money.
import { PRICING_LIMITS, type CostResult, type ExportResponse, type FrameMarkupBandRef, type PriceProposal, type ProductBranchFigures, type ProductCategory, type ProductSummary } from "@visionx/contracts";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { pricing, type PriceExportBody } from "../api/endpoints.pricing";
import { carriedOut, describeError, isApiError } from "../api/problem";
import { formatCount, formatMoney, isLocalDate, localDate, percentFromBp } from "../lib/format";
import { useOneStore } from "../session/one-store";
import { useSessionView } from "../session/store";
import { Alert, Field, Modal, Select, TextInput } from "../ui/bits";
import { markupCell } from "./CatalogueShared";
import { markupText, rangeText } from "./PricingShared";

type Priced = Pick<ProductSummary, "id" | "category" | "stocked" | "isComposite" | "madeToOrder" | "brand" | "supplier">;

/**
 * How pricing proposes a product's price (BR-PRICING-006, UC-PRICING-007 E2): a frame by its band, a contact lens, an accessory
 * or a stocked lens by its product markup, a made-to-order lens by its lab's list; a service or a composite is priced by hand.
 */
export function markupKind(p: Pick<ProductSummary, "category" | "stocked" | "isComposite">): "band" | "markup" | "list" | "none" {
  if (p.isComposite || p.category === "Service") return "none";
  if (p.category === "Frame") return "band";
  if (p.category === "ContactLens" || p.category === "Accessory") return "markup";
  return p.stocked ? "markup" : "list";
}

/**
 * UC-PRICING-007 A1: the proposal for the product's cost at the acting branch (`POST /pricing/markup/suggest`), asked only for a
 * product pricing marks up and only once the branch knows its cost; it writes nothing. A failed ask leaves the screens as they
 * were without it.
 */
export function useProposal(p: Priced | null, f: ProductBranchFigures | null) {
  const s = useSessionView();
  const branchId = s?.activeBranchId ?? null;
  const kind = p ? markupKind(p) : "none";
  const cost = f?.costCents ?? null;
  return useQuery({
    queryKey: ["pricing", "suggest", p?.id ?? "", branchId ?? "", cost ?? -1],
    queryFn: () => pricing.suggest({ branchId: branchId!, productId: p!.id, netCostCents: cost! }),
    enabled: p !== null && (kind === "band" || kind === "markup") && cost !== null && branchId !== null,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * UC-PRICING-011: a made-to-order lens's lab cost at the acting branch and the list it comes from (`GET /pricing/costs`) — or, no list
 * carrying it, its supplier price (`SupplierPrice`, no list; A3, change 9), which gives no markup. The panel's Markup names the list
 * from it; the Cost row's amount and its word are the product's own figures (`costSource`), never this answer, asked after the product
 * is read and not again on a save (UC-CATALOGUE-003 A14 step 2; the review of change 9, 1 Nov 2026, C9R-PWA-1, C9R-CONTRACT-1).
 */
export function useLabCost(p: Priced | null) {
  const s = useSessionView();
  const branchId = s?.activeBranchId ?? null;
  return useQuery({
    queryKey: ["pricing", "cost", p?.id ?? "", branchId ?? ""],
    queryFn: () => pricing.costOf(p!.id, branchId!),
    enabled: p !== null && markupKind(p) === "list" && branchId !== null,
    staleTime: 30_000,
    retry: false,
  });
}

const basisWord = (p: PriceProposal): string => (p.basis === "FrameMarkupBand" ? "band" : p.basis === "ProductMarkup" ? "markup" : "list");

/** The keys a band is for (BR-PRICING-005): the product, its supplier, its brand — or, on the default band, that its brand has none. */
function bandKeys(b: FrameMarkupBandRef, p: Priced): string {
  const keys = [b.productId ? "this product" : null, b.supplierId ? (p.supplier?.id === b.supplierId ? p.supplier.name : "its supplier") : null, b.brand].filter((k): k is string => !!k);
  if (keys.length > 0) return ` (${keys.join(" · ")})`;
  return p.brand ? ` (no ${p.brand} override)` : "";
}

/**
 * The panel's Markup row (UC-CATALOGUE-003 step 5): the band behind a frame's markup, a product markup with whose it is, a
 * made-to-order lens's list; until pricing answers — or when it has nothing to say — the list's own markup cell.
 */
export function markupRow(p: Priced, f: ProductBranchFigures | null, proposal: PriceProposal | null | undefined, lab: CostResult | undefined, branch: string | null): [ReactNode, ReactNode] {
  if (proposal?.band) {
    const b = proposal.band;
    return ["Markup band", <>{rangeText(b)} → {markupText(b.marginPercentBp, b.marginValueCents)} · <Link to="/settings/markups">{b.scope === "Branch" ? `${branch ?? "Branch"} override` : "tenant default band"}</Link>{bandKeys(b, p)}</>];
  }
  if (proposal?.productMarkup) {
    const m = proposal.productMarkup;
    return ["Markup", <>{markupText(m.percentBp, m.valueCents)} · {m.scope === "Branch" ? `${branch ?? "Branch"} override` : "organisation"}</>];
  }
  if (markupKind(p) === "list" && f?.markupBp != null && lab && !lab.priceMissing) {
    return ["Markup", <>+{percentFromBp(f.markupBp)} on lab price{lab.priceListName ? ` · ${lab.priceListName}` : ""}</>];
  }
  return ["Markup", p.category === "Service" ? <span className="faint">—</span> : markupCell(p, f, branch)];
}

/** The panel's Price (UC-PRICING-001 step 4): the price at the branch, and whether it is what the band or markup gives (Proposed there). */
export function priceWords(price: number | null, proposal: PriceProposal | null | undefined): ReactNode {
  if (price === null) return <span className="faint">No price</span>;
  const shown = <><span className="mono strong">{formatMoney(price)}</span> incl. VAT</>;
  if (!proposal) return shown;
  const what = basisWord(proposal);
  if (price === proposal.suggestedIncVatCents) return <>{shown} · from {what}</>;
  return <>{shown} · set by hand · {what} gives <span className="mono">{formatMoney(proposal.suggestedIncVatCents)}</span></>;
}

/** UC-PRICING-001 step 1, A4: beside the form's Price, what the band or markup gives for the cost, and Use to take it. */
export function ProposalHint({ proposal, costCents, onUse }: { proposal: PriceProposal; costCents: number; onUse: (cents: number) => void }) {
  const what = proposal.basis === "FrameMarkupBand" ? "Band" : "Markup";
  return (
    <div className="row c gap-8">
      <span className="small">{what} for {formatMoney(costCents)}: <span className="mono">{formatMoney(proposal.suggestedIncVatCents)}</span> ({markupText(proposal.percentBp, proposal.valueCents)})</span>
      <button className="btn sm" type="button" aria-label={`Use the ${what.toLowerCase()}'s price, ${formatMoney(proposal.suggestedIncVatCents)}`} onClick={() => onUse(proposal.suggestedIncVatCents)}>Use</button>
    </div>
  );
}

// --- the price-list export (UC-PRICING-012) ----------------------------------------------------------------------------------

/** What an export takes: the ticked products, or the tab and its filters (A1), with the words the dialog says it in. */
export type PriceSelection = { words: string } & ({ productIds: string[] } | { categories: readonly ProductCategory[]; q?: string | undefined; supplierId?: string | undefined; brand?: string | undefined; unapplied?: string | undefined });

/**
 * UC-PRICING-012 steps 1–5, A1, E1: the day and the branch — today and the acting branch, any branch the session may read — then
 * one keyed `POST /pricing/price-list-exports`. The date comes first, so Enter in it confirms the defaults from the keyboard. A
 * selection over the cap is said in the dialog, which keeps it.
 */
export function ExportPricesModal({ selection, onClose, onDone }: { selection: PriceSelection; onClose: () => void; onDone: (r: ExportResponse, branchName: string) => void }) {
  const s = useSessionView();
  const branches = s?.switchableBranches ?? [];
  // A practice of one store asks only the day: the file is its one branch's prices (UC-PRICING-012 A6; change 13).
  const oneStore = useOneStore();
  const [branchId, setBranchId] = useState(s?.activeBranchId ?? branches[0]?.id ?? "");
  const [date, setDate] = useState(() => localDate(new Date()));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const body = useMemo((): PriceExportBody | null => {
    if (!branchId || !isLocalDate(date)) return null;
    if ("productIds" in selection) return { branchId, date, productIds: selection.productIds };
    const category = selection.categories.length === 1 ? selection.categories[0]! : [...selection.categories];
    return { branchId, date, category, ...(selection.q ? { q: selection.q } : {}), ...(selection.supplierId ? { supplierId: selection.supplierId } : {}), ...(selection.brand ? { brand: selection.brand } : {}) };
  }, [branchId, date, selection]);
  // One key per export asked: a retry after a lost answer is the same export (A5), a changed day or branch a new one. An export
  // carried out whose answer was not kept (409 REQUEST_COMPLETED, BR-GOVERNANCE-016) was made, but its link is gone with the answer
  // and no route reads it again: the dialog says so, and Export then asks for a new export under a new key (A5 step 2).
  const [made, setMade] = useState(0);
  const key = useMemo(() => newIdempotencyKey(), [JSON.stringify(body), made]); // eslint-disable-line react-hooks/exhaustive-deps
  const branchName = branches.find((b) => b.id === branchId)?.name ?? "the branch";
  const submit = async () => {
    if (busy || !body) return;
    setBusy(true);
    setError(null);
    try { onDone((await pricing.exportPrices(body, key)).data, branchName); }
    catch (err) { if (carriedOut(err)) setMade((n) => n + 1); setError(err); }
    finally { setBusy(false); }
  };
  const tooMany = isApiError(error) && error.code === "VALIDATION_FAILED" && error.errors.some((f) => f.path === "range" && f.code === "too_many_rows");
  const max = isApiError(error) && typeof error.meta.max === "number" ? error.meta.max : PRICING_LIMITS.exportRowCap;
  const counted = isApiError(error) && typeof error.meta.rowCount === "number" ? error.meta.rowCount : null;
  // A tab's selection over the cap is answered as the cap and one more — more than the cap, not a count (as built).
  const over = "productIds" in selection && counted !== null ? formatCount(counted) : `more than ${formatCount(max)}`;
  return (
    <Modal title="Export prices (CSV)" meta={selection.words} onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={busy || !body}>{busy ? "Exporting…" : "Export"}</button>
    </>}>
      {tooMany
        ? <Alert tone="crit" role="alert"><b>That selects {over} products; export at most {formatCount(max)} — narrow the filter.</b></Alert>
        : carriedOut(error) ? <Alert tone="warn" role="alert"><b>Exported, but its link did not arrive.</b> Export again for a new link.</Alert>
          : error !== null && <Alert tone="crit" role="alert"><b>Not exported.</b> {describeError(error)}</Alert>}
      <div className="grid-2">
        <Field label="Prices on" htmlFor="export-date" error={isLocalDate(date) ? undefined : "Choose a day"}>
          <TextInput id="export-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        {!oneStore && (
          <Field label="Branch" htmlFor="export-branch">
            <Select id="export-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          </Field>
        )}
      </div>
      <p className="small">One CSV of {branchName}'s prices that day, as its lines would be priced without a serial: SKU, description, price in cents, where the price comes from, VAT treatment, branch and date, in SKU order. Recorded in the audit log.</p>
      {"unapplied" in selection && selection.unapplied && <p className="small faint">{selection.unapplied}</p>}
    </Modal>
  );
}
